import { OrderedResponseBuffer } from '../internal/ordered-response-buffer';
import { ResponseStream } from '../internal/response-stream';
import {
    IResponseBuffer,
    IResponseStream,
    ITaskService
} from '../types/internal-types';
import {
    AIRequest,
    AIResponse,
    AITask,
    AITaskStatus,
    InvalidTaskStateError,
    ResponseTimeoutError,
    TaskValidationError,
    UnsupportedResponseError
} from '../types/public-types';
import {
    ILogger,
    IStreamingProvider,
    IStreamingProviderFactory,
    CommunicationProtocol
} from '~/packages/core';
import { AITaskResponse } from '../types/server-types';
import { GeneratorFanout } from '../internal/generator-fanout';
import { getUnsupportedTypeData } from '../validators/response-validator';

interface HoneybookAITaskParams {
    taskResponse: AITaskResponse;
    taskClient: ITaskService;
    streamingProviderFactory: IStreamingProviderFactory;
    timeout?: number;
    logger: ILogger;
    /**
     * The order number of the last response that was processed
     * Used for filtering when reconnecting to existing tasks
     */
    lastProcessedOrder?: number;
}

// Constants for exponential backoff configuration
export const EXPONENTIAL_BACKOFF_FACTOR = 1.6;
export const MAX_RETRIES = 7;

export class HoneybookAITask implements AITask {
    private streamingProvider: IStreamingProvider<AITaskResponse>;
    private buffer: IResponseBuffer;
    private stream: IResponseStream;
    private _status: AITaskStatus = 'active';
    private taskResponse: AITaskResponse;
    private retryCount: number = 0;
    private readonly maxRetries = MAX_RETRIES;
    private readonly _timeout: number;
    private readonly taskClient: ITaskService;
    private readonly streamingProviderFactory: IStreamingProviderFactory;
    private readonly logger: ILogger;
    private fanout: GeneratorFanout<AIResponse>;
    // The order number of the last response that was processed
    // Starts with initial value for reconnection, gets updated as we process responses
    private _lastProcessedOrder: number;
    // Array of all responses received so far
    private _responses: AIResponse[];

    get status() {
        return this._status;
    }

    get id() {
        return this.taskResponse.ai_task_id;
    }

    get feature() {
        return this.taskResponse.feature;
    }

    get timeout() {
        return this._timeout;
    }

    get lastProcessedOrder() {
        return this._lastProcessedOrder;
    }

    get responses() {
        return [...this._responses];
    }

    constructor({
        taskResponse,
        taskClient,
        streamingProviderFactory,
        timeout = 1000,
        logger,
        lastProcessedOrder
    }: HoneybookAITaskParams) {
        if (!taskResponse.protocol) {
            throw new TaskValidationError(
                taskResponse.ai_task_id,
                'Protocol configuration required'
            );
        }

        this.taskResponse = taskResponse;
        this._timeout = timeout;
        this.taskClient = taskClient;
        this.streamingProviderFactory = streamingProviderFactory;
        this.logger = logger;
        this._lastProcessedOrder = lastProcessedOrder ?? -1;
        this._responses = taskResponse.responses
            ? [...taskResponse.responses]
            : [];

        this.init();

        if (taskResponse.responses?.length) {
            // Add ALL initial responses to buffer - don't filter here
            // The OrderedResponseBuffer will handle ordering and the stream filter will handle skipping processed ones
            this.addResponsesToBuffer(taskResponse.responses);
        }
    }

    private async initializeTransport(
        streamingProviderFactory: IStreamingProviderFactory,
        protocol: CommunicationProtocol
    ): Promise<void> {
        try {
            try {
                this.streamingProvider =
                    await streamingProviderFactory.createProvider<AITaskResponse>(
                        protocol
                    );
            } catch (error) {
                this.logger.warn(
                    `Transport creation failed for task ${this.id} with protocol ${protocol.type}. Will use HTTP fallback.`,
                    error
                );
                throw error;
            }

            try {
                await this.streamingProvider.start();
            } catch (error) {
                this.logger.warn(
                    `Transport initialization failed: could not start transport for task ${this.id} with protocol ${protocol.type}. Will use HTTP fallback.`,
                    error
                );
                throw error;
            }

            try {
                for await (const taskResponse of this.streamingProvider.stream()) {
                    if (taskResponse && taskResponse.responses?.length) {
                        this.addResponsesToBuffer(taskResponse.responses);
                    } else {
                        this.logger.warn(
                            `Task ${this.id} received empty response from streaming provider`
                        );
                    }
                }
            } catch (error) {
                this.logger.warn(
                    `Transport streaming error: response streaming failed for task ${this.id} with protocol ${protocol.type}. Will use HTTP fallback.`,
                    error
                );
                throw error;
            }
        } catch (error) {
            this.streamingProvider?.stop();
            this.streamingProvider = null;
        }
    }

    async sendRequest(request: AIRequest): Promise<void> {
        if (this._status !== 'active') {
            throw new InvalidTaskStateError(
                this.taskResponse.ai_task_id,
                'Cannot send request to completed or failed task'
            );
        }

        await this.taskClient.executeTask({
            task_id: this.id,
            requests: [request],
            feature: this.feature
        });
    }

    private async retrieveUnprocessedResponses(): Promise<AIResponse[]> {
        const taskResponse = await this.taskClient.getTask(this.id);
        if (!taskResponse.responses) {
            return [];
        }

        // Filter responses based on the highest order we've processed
        // We can rely on simple order comparison because OrderedResponseBuffer ensures
        // that responses are processed in strict sequential order
        return taskResponse.responses.filter(
            r => r.order > this._lastProcessedOrder
        );
    }

    private init(): void {
        // When reconnecting to an existing task, we need to start the buffer
        // from the next expected order to avoid waiting for already processed responses
        const startingOrder = this._lastProcessedOrder + 1;

        this.buffer = this.buffer
            ? this.buffer.clone()
            : new OrderedResponseBuffer(startingOrder);

        this.stream = new ResponseStream(
            this.buffer,
            this._timeout *
                Math.pow(EXPONENTIAL_BACKOFF_FACTOR, this.retryCount)
        );

        if (!this.streamingProvider) {
            this.initializeTransport(
                this.streamingProviderFactory,
                this.taskResponse.protocol
            );
        }
    }

    private addResponsesToBuffer(responses: AIResponse[]): void {
        // Add responses to our internal array as soon as we receive them
        // This allows the responses property to contain all responses received so far
        // (both processed and unprocessed), which is essential for reconnection scenarios
        for (const response of responses) {
            // Skip null/undefined responses to prevent errors in tests
            if (!response) {
                continue;
            }
            if (!this._responses.some(r => r.uid === response.uid)) {
                this._responses.push(response);
            }
        }
        // Keep responses sorted by order
        this._responses.sort((a, b) => a.order - b.order);

        // Add to buffer for streaming (including null responses for proper error handling downstream)
        this.buffer.push(responses);
    }

    private processResponse(response: AIResponse): void {
        // Update the highest order number we've processed
        this._lastProcessedOrder = Math.max(
            this._lastProcessedOrder,
            response.order
        );

        // Note: We don't add to _responses here anymore since addResponsesToBuffer already did that
        // when the response was first received. The responses array now contains ALL responses
        // (both processed and unprocessed), and lastProcessedOrder differentiates between them.

        // Check for unsupported data
        const unsupportedData = getUnsupportedTypeData(response);

        if (unsupportedData !== null) {
            this.logger.error(
                `Task ${this.id} received unsupported response of type ${response.type}: ${unsupportedData}`
            );

            throw new UnsupportedResponseError(response.type, unsupportedData);
        }

        if (response.type === 'done') {
            this._status = 'completed';
            this.logger.debug(`Task ${this.id} completed`);
        } else if (response.type === 'error') {
            this._status = 'error';
            this.logger.error(`Task ${this.id} failed`, response);
        }
    }

    async *responseStream(): AsyncGenerator<AIResponse, void, unknown> {
        if (this._status !== 'active') {
            throw new InvalidTaskStateError(
                this.taskResponse.ai_task_id,
                'Cannot get responses from completed or failed task'
            );
        }

        // Create the fanout to make sure we can handle multiple consumers
        this.fanout ??= new GeneratorFanout(this.createResponsesGenerator());
        // Return a consumer specific generator
        yield* this.fanout.generator();
    }

    private async *createResponsesGenerator(): AsyncGenerator<
        AIResponse,
        void,
        unknown
    > {
        try {
            while (true) {
                try {
                    for await (const response of this.stream.responses()) {
                        this.retryCount = 0;

                        // Filter out already processed responses when reconnecting to existing tasks
                        // We can rely on simple order comparison because OrderedResponseBuffer ensures
                        // that responses are delivered in strict sequential order (0, 1, 2, 3...)
                        // This makes processed responses always form a contiguous sequence from 0 to lastProcessedOrder
                        if (response.order <= this._lastProcessedOrder) {
                            continue; // Skip already processed response
                        }

                        this.processResponse(response);
                        yield response;

                        if (this._status !== 'active') {
                            return;
                        }
                    }
                    return;
                } catch (error) {
                    if (error instanceof ResponseTimeoutError) {
                        this.logger.warn(
                            `Response timeout for task ${this.id}, attempting fallback`,
                            { feature: this.feature }
                        );
                        const unprocessedResponses =
                            await this.retrieveUnprocessedResponses();

                        if (unprocessedResponses.length > 0) {
                            this.retryCount = 0;
                            this.logger.debug(
                                `Retrieved unprocessed responses for task ${this.id}:`,
                                unprocessedResponses
                            );
                        } else {
                            this.retryCount++;
                            this.logger.warn(
                                `No unprocessed responses found for task ${this.id}, retry count: ${this.retryCount}`
                            );
                        }

                        if (this.retryCount > this.maxRetries) {
                            this.logger.error(
                                `Max retries exceeded for task ${this.id}`,
                                { feature: this.feature }
                            );
                            throw error;
                        }

                        this.init();
                        this.addResponsesToBuffer(unprocessedResponses);
                    } else {
                        this.logger.error(
                            `Response stream error: Task ${this.id} encountered a non-timeout error during response processing:`,
                            error
                        );
                        throw error;
                    }
                }
            }
        } catch (error) {
            this._status = 'error';
            throw error;
        } finally {
            await this.cleanupResources();
        }
    }

    private async cleanupResources(): Promise<void> {
        await this.streamingProvider?.stop();
        this.streamingProvider = null;
        this.buffer = null;
        this.stream = null;
    }
}
